import { GroupedTimetableByVehicle } from '@/new-transport/_models/transport-timetable.model';
import { deepClone } from '@/_utils/deep-clone';

/**
 * Updates a specified property of a timetable entry within a grouped vehicle timetable.
 *
 * @template T - The type of the new value for the property to update.
 * @param {GroupedTimetableByVehicle[]} groupedVehicles - The array of grouped vehicle timetables.
 * @param {string} property - The name of the property to update within the timetable entry.
 * @param {number} vehicleId - The ID of the vehicle whose timetable will be updated.
 * @param {number} timetableId - The ID of the specific timetable entry to be updated.
 * @param {T} newValue - The new value to set for the specified property.
 * @returns {GroupedTimetableByVehicle[]} - A new array of grouped vehicle timetables with the updated property.
 *
 * @example
 * // Update the "fee" property of a timetable entry
 * const updatedTimetable = updateTimetableByProperty(
 *   vehicles,
 *   "fee",
 *   101, // vehicleId
 *   5, // timetableId
 *   40 // newValue
 * );
 */
export function updateTimetableByProperty<T = unknown>(
	groupedVehicles: GroupedTimetableByVehicle[],
	property: string,
	vehicleId: number,
	timetableId: number,
	newValue: T,
): GroupedTimetableByVehicle[] {
	return deepClone(groupedVehicles).map((grouped) => {
		if (grouped.vehicle.id !== vehicleId) {
			return grouped;
		}

		grouped.timetable.forEach((t) => {
			if (t.id === timetableId) {
				t[property] = newValue;
			}
		});

		return grouped;
	});
}
